import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { TrendingUp } from 'lucide-react';

const AXIS_TICK = { fontSize: 11 };
const TOOLTIP_CONTENT_STYLE = { background: '#0f172a', border: '1px solid #334155', borderRadius: 6 };
const TOOLTIP_LABEL_STYLE = { color: '#cbd5e1' };
const TOOLTIP_ITEM_STYLE = { color: '#fff' };
const LEGEND_STYLE = { color: '#cbd5e1', fontSize: 12 };
const BAR_RADIUS = [2, 2, 0, 0];
const CHART_CONTAINER_STYLE = { minHeight: 220 };

const fmtShortDay = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

export const StatCard = ({ icon: Icon, label, value, sub }) => (
  <Card className="bg-slate-900 border-slate-800" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3 text-gray-400 text-xs uppercase tracking-wide">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-3xl font-bold text-white mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

export const CategoryList = ({ title, data }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-white">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-xs text-gray-500">No data in this window.</p>
        ) : (
          <div className="space-y-2">
            {entries.slice(0, 6).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-gray-300 truncate pr-3">{k}</span>
                <span className="text-red-400 font-semibold tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const InboxChart = ({ timeline }) => (
  <Card className="bg-slate-900 border-slate-800">
    <CardHeader className="pb-3">
      <CardTitle className="text-base text-white flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-red-400" /> Last 30 days
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="w-full min-w-0 h-[220px]" style={CHART_CONTAINER_STYLE}>
        <ResponsiveContainer width="100%" height={220} debounce={100}>
          <BarChart data={timeline.map((d) => ({ ...d, label: fmtShortDay(d.date) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="label" stroke="#64748b" tick={AXIS_TICK} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" tick={AXIS_TICK} allowDecimals={false} />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
            />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar dataKey="contacts" name="Contacts" fill="#dc2626" radius={BAR_RADIUS} />
            <Bar dataKey="quotes" name="Quotes" fill="#f59e0b" radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </Card>
);
